import {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
  type MutableRefObject,
} from "react";
import {
  type AirCfarExerciseQuote,
  type AirCfarExerciseQuoteResponse,
  AirCfarExerciseQuoteResponseEnum,
  type QuoteSuccess,
} from "@b2bportal/air-cfar-api";
import {
  IconNameEnum,
  useCfarStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CallState,
  CoreFlightsCfarExerciseComponents,
  FlightsCfarTrackingEvent,
} from "@b2bportal/core-types";
import { Icon } from "@b2bportal/core-ui";
import { ExternalResourceLoader } from "@b2bportal/core-utilities";
import type { TrackEventRequest } from "@b2bportal/event-tracking-api";
import { AirCfarExerciseLoadingScreen } from "@components/core-flights-cfar-exercise";
import { useI18nContext } from "@hopper-b2b/i18n";

import {
  type ITrackingPropertiesRequest,
  updateTrackingPropertiesRequest,
} from "@hopper-b2b/utilities";

import type {
  QuoteFetchExternalResource,
  FulfillQuoteFetchExternalResource,
} from "../../store";
import { AirCfarExerciseComplete } from "../AirCfarExerciseComplete";
import { AirCfarExerciseConfirmCancellation } from "../AirCfarExerciseConfirmCancellation";
import { AirCfarExerciseErrorUnknown } from "../AirCfarExerciseErrorUnknown";

import defaultStyles from "./AirCfarExercise.module.scss";
import {
  AirCfarExerciseQuoteResponseLoader,
  type AirCfarExerciseQuoteResponseLoaderProps,
} from "./AirCfarExerciseQuoteResponseLoader";

export interface AirCfarExerciseProps
  extends Omit<
    AirCfarExerciseQuoteResponseLoaderProps,
    | "children"
    | "response"
    | "trackViewedCfarExerciseError"
    | "trackQuoteSuccessEvents"
  > {
  itinerarySummary: ReactNode;
  email: string;
  quoteFetchResource: QuoteFetchExternalResource;
  fulfillQuoteFetchResource: FulfillQuoteFetchExternalResource;
  fetchQuote: () => void;
  fulfillQuote: (quote: AirCfarExerciseQuote) => void;
  resetExercise: () => void;
  trackEvent: (req: TrackEventRequest) => void;
  goToDone: () => void;
}

export const AirCfarExercise = (props: AirCfarExerciseProps) => {
  const [block, ch] = useModuleBEM(
    useCfarStyles(
      CoreFlightsCfarExerciseComponents.AirCfarExercise,
      defaultStyles
    ),
    CoreFlightsCfarExerciseComponents.AirCfarExercise
  );
  const { t } = useI18nContext();

  const trackingProperiesRef = useRef({
    properties: {},
    encryptedProperties: [],
  } as ITrackingPropertiesRequest);

  const calledTrackEnterCfarExerciseFlow = useRef(false);
  const calledTrackViewedCfarExerciseError = useRef(false);
  const calledTrackCfarPolicyExercised = useRef(false);
  const calledTrackQuoteSuccessEvents = useRef(false);

  const {
    quoteFetchResource,
    fulfillQuoteFetchResource,
    fetchQuote,
    resetExercise,
    goToPrevScreen: goToPrevScreenParent,
    trackEvent,
    goToDone,
  } = props;

  const updateTrackingPropertiesRef = useCallback(() => {
    if (
      quoteFetchResource.state === CallState.Success &&
      fulfillQuoteFetchResource.state === CallState.NotCalled
    ) {
      trackingProperiesRef.current = updateTrackingPropertiesRequest(
        trackingProperiesRef.current,
        [quoteFetchResource.data.trackingProperties ?? {}]
      );

      if (
        quoteFetchResource.data.AirCfarExerciseQuoteResponse === "QuoteSuccess"
      ) {
        trackingProperiesRef.current = updateTrackingPropertiesRequest(
          trackingProperiesRef.current,
          [...quoteFetchResource.data.quotes.map((q) => q.trackingProperties)]
        );
      }
    }
  }, [quoteFetchResource, fulfillQuoteFetchResource]);

  const trackEnterCfarExerciseFlow = useCallback(() => {
    callOnce(calledTrackEnterCfarExerciseFlow, () => {
      trackEvent({
        eventName: FlightsCfarTrackingEvent.ENTER_CFAR_EXERCISE_FLOW,
        properties: trackingProperiesRef.current.properties,
        encryptedProperties: trackingProperiesRef.current.encryptedProperties,
      });
    });
  }, [trackEvent]);

  const trackViewedCfarExerciseError = useCallback(() => {
    callOnce(calledTrackViewedCfarExerciseError, () => {
      trackEvent({
        eventName: FlightsCfarTrackingEvent.VIEWED_CFAR_EXERCISE_ERROR,
        properties: trackingProperiesRef.current.properties,
        encryptedProperties: trackingProperiesRef.current.encryptedProperties,
      });
    });
  }, [trackEvent, trackingProperiesRef]);

  const trackCfarPolicyExercised = useCallback(() => {
    callOnce(calledTrackCfarPolicyExercised, () => {
      trackEvent({
        eventName: FlightsCfarTrackingEvent.CFAR_POLICY_EXERCISED,
        properties: trackingProperiesRef.current.properties,
        encryptedProperties: trackingProperiesRef.current.encryptedProperties,
      });
    });
  }, [trackEvent]);

  const trackQuoteSuccessEvents = useCallback(
    (data: QuoteSuccess) => {
      callOnce(calledTrackQuoteSuccessEvents, () => {
        const exerciseQuotes = data.quotes;
        if (exerciseQuotes.length > 1) {
          trackEvent({
            eventName: FlightsCfarTrackingEvent.CFAR_EXERCISE_QUOTE_OUTCOMES,
            ...trackingProperiesRef.current,
          });
        }

        trackEvent({
          eventName: FlightsCfarTrackingEvent.CFAR_EXERCISE_QUOTE,
          ...trackingProperiesRef.current,
        });
      });
    },
    [trackEvent]
  );

  updateTrackingPropertiesRef();

  useEffect(() => {
    if (
      quoteFetchResource.state === CallState.NotCalled &&
      fulfillQuoteFetchResource.state === CallState.NotCalled
    ) {
      fetchQuote();
    }
  }, [quoteFetchResource, fulfillQuoteFetchResource, fetchQuote]);

  useEffect(
    function tracking() {
      if (
        quoteFetchResource.state === CallState.NotCalled &&
        fulfillQuoteFetchResource.state === CallState.NotCalled
      ) {
        trackEnterCfarExerciseFlow();
      }

      switch (quoteFetchResource.state) {
        case CallState.Failed: {
          trackViewedCfarExerciseError();
          break;
        }
        case CallState.Success: {
          switch (quoteFetchResource.data.AirCfarExerciseQuoteResponse) {
            case AirCfarExerciseQuoteResponseEnum.QuoteSuccess:
              trackQuoteSuccessEvents(quoteFetchResource.data);
              break;
            case AirCfarExerciseQuoteResponseEnum.QuoteFailure:
              trackViewedCfarExerciseError();
              break;
          }
          break;
        }
      }

      switch (fulfillQuoteFetchResource.state) {
        case CallState.Failed: {
          trackViewedCfarExerciseError();
          break;
        }
        case CallState.Success: {
          trackCfarPolicyExercised();
          break;
        }
      }
    },
    [
      quoteFetchResource,
      fulfillQuoteFetchResource,
      trackViewedCfarExerciseError,
      trackQuoteSuccessEvents,
      trackCfarPolicyExercised,
      trackEnterCfarExerciseFlow,
    ]
  );

  const goToPrevScreen = useCallback(() => {
    resetExercise();
    goToPrevScreenParent();
  }, [resetExercise, goToPrevScreenParent]);

  const resetAndDone = useCallback(() => {
    resetExercise();
    goToDone();
  }, [resetExercise, goToDone]);

  return (
    <div className={block}>
      <div className={ch("header")}>
        <Icon
          className={ch("header-icon")}
          iconName={IconNameEnum.cancelForAnyReason}
          useCurrentColor={false}
        />
        <h2 className={ch("header-title")}>
          {t("core-flights-cfar.exercise.main.title")}
        </h2>
      </div>
      <div className={ch("body")}>
        <BodyWrapper
          {...props}
          goToPrevScreen={goToPrevScreen}
          goToDone={resetAndDone}
        />
      </div>
    </div>
  );
};

const BodyWrapper = (props: AirCfarExerciseProps): JSX.Element => {
  const {
    quoteFetchResource,
    fulfillQuoteFetchResource,
    fulfillQuote,
    goToRegularCancellationFlow,
    goToPrevScreen,
    goToContactSupport,
    termsAndConditionsLink,
    email,
    itinerarySummary,
    goToDone,
  } = props;

  const { t } = useI18nContext();

  const confirmCanceletion = useCallback(
    (quote: AirCfarExerciseQuote) => {
      fulfillQuote(quote);
    },
    [fulfillQuote]
  );

  const fulfillCommandFailedErrorElement = useMemo(
    () => (
      <AirCfarExerciseErrorUnknown
        onContactSupportClick={goToContactSupport}
        onGoBackClick={goToPrevScreen}
      />
    ),
    [goToContactSupport, goToPrevScreen]
  );

  const currentScreenId = useMemo(() => {
    if (fulfillQuoteFetchResource.state === CallState.NotCalled) {
      return "fetchQuote";
    }

    return "fulfillQuote";
  }, [fulfillQuoteFetchResource]);

  switch (currentScreenId) {
    case "fetchQuote":
      return (
        <ExternalResourceLoader
          data={quoteFetchResource}
          notCalled={
            <AirCfarExerciseLoadingScreen
              title={t(
                "core-flights-cfar.exercise.main.quoteFetchLoadingTitle"
              )}
            />
          }
          loading={
            <AirCfarExerciseLoadingScreen
              title={t(
                "core-flights-cfar.exercise.main.quoteFetchLoadingTitle"
              )}
            />
          }
          failed={() => fulfillCommandFailedErrorElement}
        >
          {(response: AirCfarExerciseQuoteResponse) => (
            <AirCfarExerciseQuoteResponseLoader
              response={response}
              termsAndConditionsLink={termsAndConditionsLink}
              goToContactSupport={goToContactSupport}
              goToPrevScreen={goToPrevScreen}
              goToRegularCancellationFlow={goToRegularCancellationFlow}
            >
              {(data) => (
                <FetchedQuote
                  quote={data}
                  itinerarySummary={itinerarySummary}
                  goToPrevScreen={goToPrevScreen}
                  goToContactSupport={goToContactSupport}
                  confirmCanceletion={confirmCanceletion}
                />
              )}
            </AirCfarExerciseQuoteResponseLoader>
          )}
        </ExternalResourceLoader>
      );
    case "fulfillQuote":
      return (
        <ExternalResourceLoader
          data={fulfillQuoteFetchResource}
          failed={() => {
            return fulfillCommandFailedErrorElement;
          }}
          loading={
            <AirCfarExerciseLoadingScreen
              title={t(
                "core-flights-cfar.exercise.main.fulfillCommandLoadingTitle"
              )}
              subtitle={t(
                "core-flights-cfar.exercise.main.fulfillCommandLoadingSubtitle"
              )}
            />
          }
          notCalled={fulfillCommandFailedErrorElement}
        >
          {() => (
            <AirCfarExerciseComplete email={email} onDoneClick={goToDone} />
          )}
        </ExternalResourceLoader>
      );
  }
};

const FetchedQuote = (props: {
  quote: QuoteSuccess;
  itinerarySummary: ReactNode;
  goToPrevScreen: () => void;
  goToContactSupport: () => void;
  confirmCanceletion: (quote: AirCfarExerciseQuote) => void;
}) => {
  const {
    quote,
    itinerarySummary,
    goToPrevScreen,
    goToContactSupport,
    confirmCanceletion,
  } = props;

  switch (quote.quotes.length) {
    case 0:
      return (
        <AirCfarExerciseErrorUnknown
          onContactSupportClick={goToContactSupport}
          onGoBackClick={goToPrevScreen}
        />
      );
    case 1:
      return (
        <AirCfarExerciseConfirmCancellation
          quote={quote.quotes[0]}
          itinerarySummary={itinerarySummary}
          onGoBackClick={goToPrevScreen}
          onContactSupportClick={goToContactSupport}
          onConfirmCancellationClick={() => confirmCanceletion(quote.quotes[0])}
        />
      );
    default:
      console.error("FetchedQuote: multiple quotes is not implemented", quote);
      return (
        <AirCfarExerciseErrorUnknown
          onContactSupportClick={goToContactSupport}
          onGoBackClick={goToPrevScreen}
        />
      );
  }
};

function callOnce(called: MutableRefObject<boolean>, fn: () => void) {
  if (called.current) {
    return;
  }
  fn();
  called.current = true;
}
